import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { cardLinkProps, isModifiedClick } from "../utils/cardLink";
import { formatDuration } from "../utils/helpers";
import { Play, Volume2, VolumeX, Eye } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import apiService from "../apiService";
import PremiumBanner from "../components/PremiumBanner";
import { useT } from "../i18n/useT";
import { useHoverPreview } from "../components/useHoverPreview";
// The big blocks move on their own; see the note in the hook for why they do not
// use the hover rule the small cards use.
import { useAmbientPreview } from "../components/useAmbientPreview";
import {
  ROW_TITLE_FONT,
  cleanTitle,
  safeRenderLatex,
  CLAMP_2_LINES,
  TITLE_SCRIM,
} from "../utils/videoTitle";

// Page inset. Matches the Workspace container's px-6 so both pages start at the same
// left edge and get the same measure; the old 4% resolved to 55px at 1440 and made
// Explore visibly narrower than the Workspace beside it.
const EDGE = "24px";

const THUMBNAIL_FILTER = "saturate(1.25) contrast(1.08) brightness(1.02)";

/* WHERE EACH HERO BANNER STARTS PLAYING. Not at 0, and not at one number either.
 *
 *  A lesson opens on its title card being written onto an empty frame, so frame 0 of every
 *  banner is a black rectangle — measured on all five `explore_hero` renders, 0.00% lit
 *  pixels at t=0, every one. The banner was explicitly pinned there (`currentTime = 0`), so
 *  it faded a black video in over a perfectly good thumbnail and looked frozen.
 *
 *  WHY A TABLE AND NOT A CONSTANT. The first fix was the home page chip's flat 1.5s
 *  (`DEMO_PREVIEW_START_SECONDS`), which clears the black but on four of the five lands on
 *  the title line alone — the lesson has not drawn anything yet. Where a lesson's opening
 *  goes is written by the lesson, so the only honest form is one measured number per video.
 *
 *  EVERY ENTRY IS A KEYFRAME, AND THAT IS THE POINT. Seeking is a byte-range request plus a
 *  decode forward from the preceding keyframe, so cost is DISTANCE TO THAT KEYFRAME, not
 *  position in the file: 30.0s in the Fourier banner costs 750KB because it sits 4.6s past
 *  one, where 22.15s costs 247KB because it IS one. Land on a keyframe and the browser
 *  decodes a single self-contained image.
 *
 *  NOT, HOWEVER, "SNAP TO THE NEAREST KEYFRAME" — that rule picks a black frame half the
 *  time. Keyframes here come in pairs, the scene cut and then its content a beat later, and
 *  34 of the 68 across these five videos are blank. The cheap ones are cheap BECAUSE they
 *  are black. The rule is "the cheapest keyframe that is worth looking at", which cannot be
 *  evaluated at runtime (it needs the moov atom) and so lives here as measured values.
 *
 *  APPLIED AS A MEDIA FRAGMENT, not a seek. `#t=` tells the browser where to begin, so the
 *  first frame it ever paints is the right one; setting `currentTime` after load paints
 *  frame 0 and then jumps, which is the thing being fixed.
 *
 *  Re-measure with the ids below, which are the 4k banner RENDERS — not the catalogue copies
 *  of the same lessons. "Visualizing the Fourier Transform" exists twice and matching it by
 *  title gets the 1440p/60 version the home page uses. `explore_hero` in Firestore is the
 *  authority; `scripts/.banner_generation_state.json` records which id is which. */
const HERO_START_OVERRIDES = {
  "7ab52c30-abea-4c23-b78b-c06b9e38ea2f": 22.15,  // Fourier Transform — two labelled waves
  "373f783f-3c5d-4fc5-9f22-2f5927966a2d": 10.44,  // Neural Networks — the perceptron, wired
  "238c08a5-f134-41c6-923e-7b9c9750a9bf": 25.02,  // Eigenvalues — the transformed grid
  "14a37921-ec76-4fae-ac9e-ea528a041bbb": 10.44,  // Black Holes — the curved sheet
  "38ce0def-903f-4813-8771-6bbab123e734": 10.44,  // Electron Orbitals — the orbital cloud
};

/* THE FALLBACK, for a hero this table has not seen. Every current `explore_hero` video is
 *  listed above, so this fires only when a new banner is generated, or when the curated
 *  endpoint falls back to the whole featured pool because nothing is flagged. 1.5s is the
 *  home chip's number and the safe general answer: it clears the opening black on every
 *  lesson rendered by this pipeline, which is the failure actually worth preventing. */
const HERO_START_SECONDS = 1.5;

const heroStartFor = (video) =>
  HERO_START_OVERRIDES[video?.job_id || video?.video_id] ?? HERO_START_SECONDS;

// --- VideoCard: thumbnail that plays IN PLACE on hover, muted (YouTube-style) ---
// Deliberately not the Netflix treatment: no growing, no floating card, no layout shift.
// The card keeps its exact box and the video fades in over the still.
const VideoCard = ({ video, onClick }) => {
  const t = useT();
  // Hover preview lives in a shared hook — the Workspace grid uses the same one.
  const { hoverProps, preview } = useHoverPreview(
    video.download_url,
    "absolute inset-0 w-full h-full object-cover transition-opacity duration-300",
  );

  return (
    // Real link, not a button: without an href the browser offers no "Open in New Tab",
    // no Cmd-click, and no URL on hover. cardLinkProps keeps SPA navigation on a plain
    // click and lets the browser own modified clicks.
    <a
      {...cardLinkProps(`/video/${video.job_id}`, () => onClick(video))}
      {...hoverProps}
      className="flex-shrink-0 text-left"
      style={{ width: "calc((100% - 2 * 6px) / 3)", minWidth: 200 }}
    >
      <div
        className="aspect-video overflow-hidden relative rounded-xl"
        style={{
          filter: THUMBNAIL_FILTER,
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        }}
      >
        {video.thumbnail_url ? (
          <img
            src={video.thumbnail_url}
            alt={cleanTitle(video.title)}
            className="w-full h-full object-cover"
            loading="lazy"
            style={{ imageRendering: "-webkit-optimize-contrast", backfaceVisibility: "hidden" }}
          />
        ) : (
          <div className="w-full h-full bg-slate-200 flex items-center justify-center">
            <span className="text-slate-400 text-xs">{t('explore_no_preview')}</span>
          </div>
        )}

        {/* Scrim + title live INSIDE the frame, above both the still and the hover video,
            so the label survives the preview starting rather than vanishing under it. */}
        <div
          className="absolute inset-0 pointer-events-none z-10"
          style={{ background: TITLE_SCRIM }}
        />
        {/* Padding on the WRAPPER, never on the clamped <p> — see CLAMP_2_LINES. */}
        <div className="absolute bottom-0 left-0 right-0 z-10 p-2.5 pointer-events-none">
          <p
            className="text-white text-sm sm:text-base font-bold leading-snug drop-shadow [&_.katex]:text-[0.9em]"
            style={{ fontFamily: ROW_TITLE_FONT, ...CLAMP_2_LINES }}
            dangerouslySetInnerHTML={{ __html: safeRenderLatex(video.title) }}
          />
        </div>

        {preview}
      </div>
    </a>
  );
};

// --- SearchGrid: results in the SAME language as the rows ---
// Search used to hand off to SearchResults.jsx, a separate 5-up grid at max-w-[1800px] with
// captions UNDER the cards. Same page, different card, different width, different inset — so
// searching felt like leaving Explore. This renders results with Explore's own VideoCard so
// only the arrangement changes: a wrapping grid rather than a scroll track, because results
// are a set you scan, not a curated line you slide through.
const SearchGrid = ({ query, onVideoClick }) => {
  const t = useT();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiService
      .searchPublicVideos(query, 60, 0)
      .then((res) => {
        if (cancelled) return;
        setVideos(res?.videos || res?.results || []);
      })
      .catch(() => { if (!cancelled) setVideos([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [query]);

  return (
    <section className="mb-8 pt-4">
      <h2
        className="text-slate-900 text-xl mb-3"
        style={{
          fontFamily: ROW_TITLE_FONT,
          fontWeight: 700,
          lineHeight: 1.3,
          WebkitFontSmoothing: "antialiased",
          paddingLeft: EDGE,
        }}
      >
        {`\u201C${query}\u201D`}
      </h2>

      <div
        className="flex flex-wrap items-start gap-[6px]"
        style={{ paddingLeft: EDGE, paddingRight: EDGE }}
      >
        {loading
          ? Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="aspect-video rounded-xl bg-slate-200 animate-pulse flex-shrink-0"
                style={{ width: "calc((100% - 2 * 6px) / 3)", minWidth: 200 }}
              />
            ))
          : videos.map((video) => (
              <VideoCard
                key={video.job_id || video.video_id || video.id}
                video={video}
                onClick={onVideoClick}
              />
            ))}
      </div>

      {!loading && videos.length === 0 && (
        <p className="text-slate-500 text-sm" style={{ paddingLeft: EDGE }}>
          {t("explore_no_results") || "No results"}
        </p>
      )}
    </section>
  );
};

// --- VideoRow: section title + horizontal scroll track ---
const VideoRow = ({ title, videos, onVideoClick }) => {
  // No scroll-position state: the overlay arrow buttons are gone, and they were the only
  // thing that needed to know whether the track was at either end. A scroll listener firing
  // on every row of the page to drive nothing is pure cost.
  const scrollRef = useRef(null);

  if (!videos.length) return null;

  return (
    <section className="mb-8">
      <h2
        className="text-slate-900 text-xl mb-3"
        style={{
          fontFamily: ROW_TITLE_FONT,
          fontWeight: 700,
          lineHeight: 1.3,
          WebkitFontSmoothing: "antialiased",
          paddingLeft: EDGE,
        }}
      >
        {title}
      </h2>

      {/* No overlay scroll buttons. They sat as translucent white bars over the first and
          last card of every row, which is what made the edges look washed out. The rows are
          still scrollable by trackpad, wheel and touch. */}
      <div className="relative">
        <div
          ref={scrollRef}
          className="flex items-start gap-[6px] overflow-x-auto"
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            paddingLeft: EDGE,
            paddingRight: EDGE,
          }}
        >
          {videos.map((video) => (
            <VideoCard
              key={video.job_id}
              video={video}
              onClick={onVideoClick}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

/* ── FEATURE BLOCKS ──────────────────────────────────────────────────────────────────────
 *
 *  Explore is eight scroll tracks of identically-sized cards. That is a good way to show a
 *  catalogue and a bad way to hold attention: past the second row the eye has nothing to
 *  catch on, and everything below reads as more of the same. The hero solves it once, at the
 *  top, and cannot be repeated — a second full-bleed banner halfway down is just the top of
 *  the page again.
 *
 *  So: three blocks of a DIFFERENT SHAPE to the rows, dropped between them. Not banners —
 *  none is full width or full bleed, and none autoplays. They break the grid, which is the
 *  whole job.
 *
 *  THERE IS ONE FORM, MIRRORED. There were two — the split below, and a course laid out as a
 *  strip of its lesson covers — and the variety was the stated point of having both. The
 *  course block is gone, and the page is better for it: three blocks of one geometry,
 *  alternating sides, is a rhythm, where two geometries at four sizes was a grab bag.
 *
 *  THEY SHOW WHAT THE ROWS DO NOT. The pool deliberately excludes the first few cards of
 *  every row, because those are already on screen without scrolling — featuring one of them
 *  would be printing the same card twice at two sizes. A block earns its space by surfacing
 *  something the page was otherwise hiding behind a horizontal scroll.
 */

/** How many cards of a row are visible before anyone scrolls it. Cards are a third of the
 *  track, so three — anything beyond this is the hidden tail the blocks draw from. */
const VISIBLE_PER_ROW = 3;

/* ── WHERE THE BLOCKS GO, AND WHAT EACH ONE IS ───────────────────────────────────────
 *
 *  Written out as a list rather than derived from a cadence, and that is a correctness
 *  choice. The derived version was `floor((rows - 1) / N)` slots filled from a rotation,
 *  and it silently produced FEWER SLOTS THAN FORMS twice — the course block and then the
 *  drawn band were each added to the end of a rotation that never reached them, so both
 *  were dead code that looked live. A list cannot hide that: the slots and the forms are
 *  the same array, so a form without a home is visible in the thing you are reading.
 *
 *  `after` is the index of the row the block follows, so the shape of the page is legible
 *  here, in order, without running it.
 */
const FEATURE_PLAN = [
  { after: 1 },   // one lesson, large, after Popular and the first subject
  { after: 3 },
  { after: 5 },
];

/* ONE FORM, THREE SLOTS — and that is the whole plan now.
 *
 * There used to be a `kind`, because there used to be a second form: a course block that
 * laid a featured course out as a strip of its lesson covers. It is gone, so the field is
 * gone with it rather than left as a constant that reads like a choice.
 *
 * THE SPACING. The list was 0/2/4/6, which put the first big block directly under the
 * banner with a single row between them — two full-width objects stacked, so the page led
 * with furniture instead of with the catalogue. Every slot moved down one, giving two rows
 * before the first block and two between each pair.
 *
 * That caps it at three. The feed is at most eight rows (Popular plus the seven subjects in
 * CATEGORY_ORDER) and `after` must leave a row behind it, or the block lands below the whole
 * catalogue — so `after: 7` can never happen. It is left OUT rather than written down and
 * silently dropped, which is the bug the note above says this list exists to prevent. */

/* ── Form 1: the split. One still at size, a column of type beside it ──────────────────
 *  The side alternates so two splits on one page are not the same picture twice.
 *
 *  NOTHING HERE MOVES ON HOVER, AND THAT IS THE POINT. The still used to zoom to 1.02 under
 *  a `group-hover:scale`, which is a fine gesture on a 300px card and a bad one on a block
 *  this size: 2% of a ~900px frame is 18px of travel, so the picture visibly swelled past
 *  its own corners and the whole feature read as a balloon. A grey `bg-slate-900/10` wash
 *  came with it and only made the artwork look dirty.
 *
 *  What replaces it is depth, not scale: the shadow under the picture deepens and the pill
 *  beside it darkens a shade. The box never changes size, so the page never breathes under
 *  the cursor. The pill is brand indigo AT REST — it was near-black and only turned indigo
 *  on hover, which hid the one piece of colour the block has until you went looking for it.
 *  Hover state is driven by `group` on the ROW, not on the picture — the still and the type
 *  beside it are one object, so reaching for either lights both.
 */

/** View count on a feature block, or null. Deliberately silent below a hundred: "4 views"
 *  printed at this size undersells the thing it is supposed to be recommending. */
const featureViews = (n) => {
  if (!n || n < 100) return null;
  return n >= 1000 ? `${(n / 1000).toFixed(n < 10000 ? 1 : 0)}k` : `${n}`;
};

const SplitFeature = ({ video, flip, onVideoClick }) => {
  const t = useT();
  const { hostRef, preview } = useAmbientPreview(
    video.download_url,
    "absolute inset-0 w-full h-full object-cover transition-opacity duration-500",
  );
  const href = `/video/${video.job_id}`;
  const open = () => onVideoClick(video);
  const runtime = formatDuration(video.duration);
  const views = featureViews(video.view_count);

  return (
    <section className="mb-12 md:mb-14" style={{ paddingLeft: EDGE, paddingRight: EDGE }}>
      <div className={`group flex flex-col gap-5 md:gap-10 md:items-center ${flip ? "md:flex-row-reverse" : "md:flex-row"}`}>
        <a {...cardLinkProps(href, open)} className="block md:flex-[3] min-w-0">
          {/* Two nested boxes on purpose. `filter` applies to an element's ENTIRE rendering,
              box-shadow included, so saturate+contrast on the same div as the shadow tints
              the shadow too. The outer box owns the depth, the inner box owns the picture. */}
          <div
            ref={hostRef}
            className="relative rounded-2xl transition-shadow duration-500 ease-out
                       shadow-[0_1px_2px_rgba(15,23,42,0.08),0_10px_30px_-12px_rgba(15,23,42,0.22)]
                       group-hover:shadow-[0_2px_4px_rgba(15,23,42,0.10),0_26px_54px_-18px_rgba(15,23,42,0.38)]"
          >
            <div
              className="aspect-video overflow-hidden rounded-2xl relative bg-[#0f172a]"
              style={{ filter: THUMBNAIL_FILTER }}
            >
              {video.thumbnail_url && (
                <img src={video.thumbnail_url} alt="" loading="lazy"
                     className="w-full h-full object-cover" />
              )}
              {preview}
            </div>

            {/* The hairline sits OUTSIDE the filtered box so it is not saturated along with
                the artwork. No play glyph over the picture — the still already moves on its
                own here, and a button floating over a playing video is one cue too many. */}
            <span className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-slate-900/[0.07] pointer-events-none" />
          </div>
        </a>

        {/* The type column is ONE link, headline included — a headline this size that is not
            clickable is a dead target, and the pill below it was carrying the whole block. */}
        <a {...cardLinkProps(href, open)} className="block md:flex-[2] min-w-0">
          {video.explore_category && (
            <div className="flex items-center gap-2.5 mb-3.5">
              <span className="h-px w-7 bg-[#635BFF]" />
              <span className="text-[#635BFF] text-[10.5px] font-extrabold uppercase tracking-[0.22em]">
                {video.explore_category.replace(/_/g, " ")}
              </span>
            </div>
          )}

          {/* Fluid, tight and heavy. A flat text-3xl drifted smaller the wider the window
              got, next to a picture that is sized from the viewport — so the headline kept
              losing to its own still. Negative tracking is what makes big Nunito look set
              rather than merely large. */}
          <h3
            className="text-slate-900 [&_.katex]:text-[0.9em]"
            style={{
              fontFamily: ROW_TITLE_FONT,
              fontSize: "clamp(25px, 2.5vw, 38px)",
              fontWeight: 800,
              lineHeight: 1.08,
              letterSpacing: "-0.021em",
              textWrap: "balance",
            }}
            dangerouslySetInnerHTML={{ __html: safeRenderLatex(cleanTitle(video.title || "")) }}
          />

          {(runtime || views) && (
            <p className="mt-4 flex items-center gap-2.5 text-slate-400 text-[12.5px] font-semibold tracking-[0.02em]"
               style={{ fontVariantNumeric: "tabular-nums" }}>
              {runtime && <span>{runtime}</span>}
              {runtime && views && <span className="w-1 h-1 rounded-full bg-slate-300" />}
              {/* An icon, not the word "views" — this line exists in eleven languages and a
                  glyph needs none of them. */}
              {views && (
                <span className="inline-flex items-center gap-1.5">
                  <Eye size={13} strokeWidth={2.25} /> {views}
                </span>
              )}
            </p>
          )}

          {/* A span, not a nested anchor — the column above it is already the link. Centred
              in the column: the pill is the one object in this block with no left edge to
              line up against, and sitting it under the middle of the headline reads as
              deliberate where flush-left read as the end of a paragraph. */}
          <span
            className="mt-7 mx-auto flex w-fit items-center gap-2.5 pl-5 pr-6 py-3 rounded-full
                       bg-[#635BFF] text-white text-[13px] font-bold tracking-[0.015em]
                       transition-all duration-300
                       shadow-[0_2px_5px_rgba(99,91,255,0.22),0_16px_30px_-10px_rgba(99,91,255,0.55)]
                       group-hover:bg-[#5851e0]
                       group-hover:shadow-[0_3px_7px_rgba(88,81,224,0.3),0_22px_40px_-12px_rgba(88,81,224,0.65)]"
          >
            <Play size={12} fill="white" strokeWidth={0} /> {t("explore_watch_now")}
          </span>
        </a>
      </div>
    </section>
  );
};

const SkeletonRow = () => (
  <div className="mb-8">
    <div
      className="h-5 w-32 bg-slate-200 rounded mb-3 animate-pulse"
      style={{ marginLeft: EDGE }}
    />
    <div
      className="flex gap-[6px] overflow-hidden"
      style={{ paddingLeft: EDGE }}
    >
      {Array.from({ length: 7 }).map((_, i) => (
        <div key={i} className="flex-shrink-0"
             style={{ width: "calc((100% - 2 * 6px) / 3)", minWidth: 200 }}>
          <div className="aspect-video bg-slate-200 animate-pulse rounded-xl" />
        </div>
      ))}
    </div>
  </div>
);

// --- HeroBanner: full-width video preview ---
const HeroBanner = ({ video, onPlay, forceMuted, isVisible }) => {
  const t = useT();
  const videoRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true);
  const [videoReady, setVideoReady] = useState(false);
  const startAt = heroStartFor(video);

  // Only play (and auto-pause after 15s) when the page is actually visible
  useEffect(() => {
    if (!isVisible || !videoRef.current) return;
    /* REWIND ONLY ON A RETURN VISIT. The `#t=` fragment on the src owns the cold start and
       is the whole point of it — writing `currentTime` before the element has data would
       hand the start position back to the code path the fragment exists to avoid. Once it
       HAS data (coming back to Explore from another tab), the element is parked wherever it
       was paused, and a seek is both correct and free. */
    if (videoRef.current.readyState >= 1) {
      videoRef.current.currentTime = startAt;
    }
    videoRef.current.play().catch(() => {});
    const timer = setTimeout(() => {
      if (videoRef.current) videoRef.current.pause();
    }, 15000);
    return () => {
      clearTimeout(timer);
      if (videoRef.current) videoRef.current.pause();
    };
  }, [isVisible, startAt]);

  useEffect(() => {
    if (forceMuted && videoRef.current && !videoRef.current.muted) {
      videoRef.current.muted = true;
      setIsMuted(true);
    }
  }, [forceMuted]);

  const title = cleanTitle(video.title || video.topic || "");

  return (
    <div
      /* THE HEIGHT IS A CLASS, NOT A STYLE, and it has to be: an inline height beats every
         stylesheet rule, so there would be no way for `short:` to get in underneath it. The
         margin stays inline because it interpolates EDGE and nothing overrides it.

         clamp(220, 40vw, 400) is the portrait and desktop shape — shorter than the old
         clamp(280, 56vw, 520), which was eating most of the fold before a single row of cards
         appeared.

         `short:h-[44dvh]` IS THE LANDSCAPE PHONE, and 40vw is actively wrong there: rotating
         an iPhone 15 makes the viewport 852x393, so 40vw asks for 341px of a 393px screen and
         the banner alone overflows the fold — top bar, category bar and the banner's own top
         padding have already spent 132px of it. 44dvh is 173px there, which ends at 305px and
         leaves the next row's title and the top of a card showing, so the page reads as
         scrollable instead of as one cropped image. dvh not vh, because vh on a phone measures
         the viewport with the URL bar hidden and would overshoot by its height. */
      className="relative overflow-hidden bg-[#0f172a] cursor-pointer rounded-2xl border border-slate-200
                 h-[clamp(220px,40vw,400px)] short:h-[44dvh]"
      style={{ margin: `0 ${EDGE}` }}
      onClick={() => onPlay(video)}
    >
      {video.thumbnail_url && (
        <img
          src={video.thumbnail_url}
          alt={cleanTitle(video.title || video.topic || "")}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ imageRendering: "-webkit-optimize-contrast", backfaceVisibility: "hidden" }}
        />
      )}

      {video.download_url && (
        <video
          ref={videoRef}
          src={`${video.download_url}#t=${startAt}`}
          muted={isMuted}
          playsInline
          preload="metadata"
          onCanPlay={() => setVideoReady(true)}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
          style={{ opacity: videoReady ? 1 : 0 }}
        />
      )}

      <div
        className="absolute bottom-0 left-0 right-0"
        style={{
          height: "60%",
          background: "linear-gradient(to top, #0f172a 0%, rgba(15,23,42,0.7) 40%, transparent 100%)",
        }}
      />

      <div className="absolute bottom-6 left-0 px-6" style={{ maxWidth: 600 }}>
        <p
          className="text-white text-xl sm:text-3xl lg:text-4xl font-bold leading-tight mb-3 drop-shadow-lg line-clamp-2"
          style={{ fontFamily: ROW_TITLE_FONT }}
        >
          {title}
        </p>
        {video.explore_category && (
          <p className="text-white/70 text-sm sm:text-base leading-relaxed mb-4 drop-shadow capitalize">
            {video.explore_category.replace(/_/g, ' ')}
          </p>
        )}
        {/* Not cardLinkProps here: the banner BEHIND this is also clickable, so the
            click must stop propagating even on a modified click — otherwise Cmd-click
            would open a new tab AND navigate the current one. */}
        <a
          href={`/video/${video.job_id}`}
          onClick={(e) => {
            e.stopPropagation();
            if (isModifiedClick(e)) return;
            e.preventDefault();
            onPlay(video);
          }}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-slate-900 text-sm font-semibold rounded-sm hover:bg-white/90 transition-colors"
        >
          <Play size={16} fill="#0f172a" /> {t('explore_watch_now')}
        </a>
      </div>

      <button
        onClick={(e) => {
          e.stopPropagation();
          if (videoRef.current) {
            videoRef.current.muted = !videoRef.current.muted;
            setIsMuted(videoRef.current.muted);
          }
        }}
        aria-label={isMuted ? "Unmute" : "Mute"}
        className="absolute bottom-6 right-6 w-9 h-9 rounded-full border border-white/40 flex items-center justify-center bg-black/30 hover:bg-black/50 transition-colors"
      >
        {isMuted ? (
          <VolumeX size={16} className="text-white" />
        ) : (
          <Volume2 size={16} className="text-white" />
        )}
      </button>
    </div>
  );
};

// --- ExplorePage: orchestrator ---
const ExplorePage = ({ isVisible }) => {
  const t = useT();
  const [rows, setRows] = useState([]);
  const [heroVideo, setHeroVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("popular");
  const navigate = useNavigate();
  const location = useLocation();
  // Explore IS the search surface: the top-right field routes here with ?q=, and a query
  // swaps the curated hero+rows for one row of results. No separate results page to bounce
  // through, so refining a search never leaves the page you were browsing.
  const query = new URLSearchParams(location.search).get("q") || "";
  const sectionRefs = useRef({});

  useEffect(() => {
    const fetchCurated = async () => {
      try {
        const result = await apiService.getExploreCurated();
        setHeroVideo(result.hero || null);
        setRows(result.categories || []);
      } catch (err) {
        console.error("Explore: failed to load curated data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchCurated();
  }, []);


  // Build tabs dynamically from the rows returned by the API
  const tabs = useMemo(() => {
    return rows.map((row) => ({ id: row.id, label: row.title }));
  }, [rows]);

  /* WHAT THE FEATURE BLOCKS ARE ALLOWED TO SHOW — see the note above SplitFeature.
   *
   *  Everything past the third card of each row, which is everything a visitor cannot see
   *  without scrolling that row sideways. Taking the head instead would print the same card
   *  twice at two sizes, which is decoration; taking the tail is the reason the block exists.
   *
   *  Deduped across rows because `popular` overlaps every subject row by construction, and
   *  the hero is excluded because it is already the largest thing on the page. */
  const featurePool = useMemo(() => {
    const seen = new Set(heroVideo?.job_id ? [heroVideo.job_id] : []);
    const pool = [];
    for (const row of rows) {
      for (const v of (row.videos || []).slice(VISIBLE_PER_ROW)) {
        if (!v.job_id || seen.has(v.job_id)) continue;
        seen.add(v.job_id);
        pool.push(v);
      }
    }
    return pool;
  }, [rows, heroVideo]);

  /* WHICH BLOCK GOES WHERE, decided once for the whole page rather than per row.
   *
   *  Resolved up front so each block can CLAIM its video and the next one cannot show it
   *  again — doing this inline in the render would need a mutable cursor, which would run
   *  twice under StrictMode and double-consume the pool. A slot with no video left simply
   *  does not appear; it never renders an empty frame.
   *
   *  The side alternates, so the mirrored one is the middle of three rather than an odd one
   *  out at the bottom of the page. */
  const features = useMemo(() => {
    const slots = [];
    let cursor = 0;

    for (const step of FEATURE_PLAN) {
      // A plan entry past the end of the feed simply does not happen.
      if (step.after >= rows.length - 1) continue;
      if (!featurePool[cursor]) continue;

      slots.push({ after: step.after, video: featurePool[cursor],
                   flip: slots.length % 2 === 1 });
      cursor += 1;
    }
    return slots;
  }, [rows.length, featurePool]);

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);

    // Scroll the PRECEDING row to the top, not the chosen one. Landing the chosen row hard
    // against the top edge reads as cramped and gives no sense of where you are; putting the
    // row above it at the top leaves the one you asked for sitting comfortably below the
    // fold line, with visible context above it.
    //
    // The first tab has nothing before it, so it goes to the very top of the page — the hero
    // IS its context.
    const index = tabs.findIndex((t) => t.id === tabId);
    if (index <= 0) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // Fall back to the chosen row if the previous one has not mounted (rows render as data
    // arrives), so a click never silently does nothing.
    const el = sectionRefs.current[tabs[index - 1].id] || sectionRefs.current[tabId];
    if (el) {
      // Was 64 + 48 for a top navbar and a sticky category bar. Neither exists now — the
      // navbar is a left rail and the category bar scrolls away — so clicking a topic was
      // landing 112px past the section it was aiming at.
      const offset = 16;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  const handleVideoClick = useCallback((video) => {
    navigate(`/video/${video.job_id}`, {
      state: {
        videoUrl: video.download_url,
        title: video.title,
        description: video.description,
        subtitleUrl: video.subtitle_url,
        videoId: video.job_id,
      },
    });
  }, [navigate]);

  return (
    <div className="bg-[#F8FAFC] pb-16 md:pb-0 min-h-screen">
      {/* Topic bar */}
      {tabs.length > 0 && !query && (
        <>
        {/* 24px of breathing room above the category bar, cut to 8 in landscape where the
            fold is 393px and a third of it is already navigation. */}
        <div className="h-6 short:h-2 bg-[#F8FAFC]" />
        {/* NOT sticky. It was pinned at top-16 to sit under the old top bar; with navigation
            moved to a left rail there is nothing to sit under, so it just rode down the page
            and covered the cards. */}
        <div className="z-30 bg-[#F8FAFC] border-b border-slate-200 relative pt-1">
          {/* Tabs and search share ONE row. The field used to be `fixed top-3` and was
              pushed into the right corner only so it would not land on these tabs — a
              floating box avoiding the thing it belongs beside. items-center so the pill
              sits on the tabs' optical centre rather than their text baseline. */}
          {/* min-h RESERVES THE SEARCH FIELD'S HEIGHT, same as the Workspace header.
              Navigation portals the field into the slot below one frame late (a
              requestAnimationFrame that re-runs on every page switch), so the row's height
              depends on whether the field has landed yet. The tab buttons happen to measure
              about the same 44px, which is why this shift is less obvious here than it was on
              the Workspace — but "happens to match" is not a guarantee, and it stops being
              true the moment a tab's font or padding changes. Pinning the floor makes the row
              the same height before and after the field arrives, by construction. */}
          <div className="flex items-center gap-3 min-h-[44px]" style={{ paddingRight: EDGE }}>
          <div className="flex gap-1 overflow-x-auto flex-1 min-w-0" style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch", paddingLeft: EDGE, paddingRight: "1rem" }}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                className={`px-3.5 py-3 text-[15px] font-semibold whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? "border-[#635BFF] text-[#635BFF]"
                    : "border-transparent text-slate-500 hover:text-slate-800"
                }`}
                style={{ fontFamily: ROW_TITLE_FONT }}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {/* Navigation portals the desktop search field in here (see the
              magi-search-slot lookup there). Empty on mobile, where the field renders
              inline in the nav instead. shrink-0 so a long tab list cannot squeeze it. */}
          {/* Lifted 5px off the row's true centre, deliberately. The tabs carry a 2px active
              underline at their base, so the row's geometric centre sits BELOW the optical
              centre of the tab labels — centring the pill against the box made it read as
              hanging low next to the text it lines up with. An explicit transform rather
              than margin games so the number is legible and easy to retune. */}
          <div id="magi-search-slot-explore"
               className="hidden md:block w-[380px] shrink-0 -translate-y-[5px]" />
          </div>
          {/* Scroll fade hint on right edge */}
          <div className="absolute right-0 top-0 bottom-0 w-8 pointer-events-none bg-gradient-to-l from-[#F8FAFC] to-transparent md:hidden" />
        </div>
        </>
      )}

      {query ? (
        <SearchGrid query={query} onVideoClick={handleVideoClick} />
      ) : (
      <>
      {/* Hero banner. pt-4 so the gap above the banner is in the same range as the gap below
          it (the first category row is pt-6); flush against the category bar read
          lopsided. */}
      <div className="pt-4" />
      {loading ? (
        <div
          // Same height as HeroBanner, landscape case included, or the page jumps when the
          // real banner replaces it.
          className="bg-slate-200 animate-pulse rounded-2xl h-[clamp(220px,40vw,400px)] short:h-[44dvh]"
          style={{ margin: `0 ${EDGE}` }}
        />
      ) : heroVideo ? (
        <HeroBanner video={heroVideo} onPlay={handleVideoClick} isVisible={isVisible} />
      ) : null}

      {/* NO COURSES ON THIS PAGE AT ALL — not a row of course cards above the categories,
          and not the feature block that replaced it. Explore shows lessons; courses are
          entered from the Workspace, where a sequence you are partway through belongs.
          /explore/curated still returns `courses`, harmlessly: the course page and the
          Workspace read the same field, so it is not this page's to remove. */}

      {/* Content rows */}
      <div className="pt-6">
        {loading ? (
          <>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </>
        ) : rows.length === 0 ? (
          <div className="text-center py-20 text-slate-400 text-base">
            {t('explore_no_videos')}
          </div>
        ) : (
          rows.map((row, i) => (
            <React.Fragment key={row.id}>
              <div ref={(el) => (sectionRefs.current[row.id] = el)}>
                <VideoRow
                  title={row.title}
                  videos={row.videos}
                  onVideoClick={handleVideoClick}
                />
              </div>
              {/* A block of a different shape every few rows — see the note above
                  SplitFeature for why, and `features` for which one lands where. */}
              {features
                .filter((f) => f.after === i)
                .map((f) => (
                  <SplitFeature key={`f-${i}`} video={f.video} flip={f.flip}
                                onVideoClick={handleVideoClick} />
                ))}
            </React.Fragment>
          ))
        )}
      </div>

      {/* Premium strip, at the FOOT of the page. It used to sit between the courses row and
          the first category row — a pitch placed before anyone had seen what was on offer,
          and the first thing to interrupt the browse. Down here it is the natural end of the
          scroll: you have been through the catalogue, and this is what more of it costs.
          Shown to everyone, subscribers included. */}
      {!loading && rows.length > 0 && (
        <div className="pt-4 pb-14" style={{ paddingLeft: EDGE, paddingRight: EDGE }}>
          <PremiumBanner onClick={() => window.dispatchEvent(new Event("magi:open-subscribe"))} />
        </div>
      )}

      </>
      )}

    </div>
  );
};

export default ExplorePage;
